/**
 * Карантин района на каноне — `quarantine.json` из `GET /quarantine/statuses`.
 *
 * Экран карантина и слой «Карантин» карты читают один набор; канон отвечает
 * на него лентой статусов контуров. Перевод — замена реализации источника,
 * экран не переписывается (Решение 10 `astra-on-canon`).
 *
 * Что здесь меняется по смыслу и как это сказано:
 *
 * * **зелёный по умолчанию.** Канон не пишет зелёных событий для нетронутых
 *   контуров: «зелёный» у них значит «событий не было», а не «проверено».
 *   Поэтому в список статусов попадают только контуры С СОБЫТИЯМИ, а число
 *   зелёных берётся из сводки сервера вместе с оговоркой (`default_note`);
 * * **три цвета, а не два.** Жёлтый канона — «под наблюдением» (только жёлтый, №656),
 *   красный остаётся красным (назначается только человеком);
 * * **поштучного журнала обследований района канон не отдаёт.** Обследования
 *   видны на карточке контура (`/contours/{id}`); охват района профилактикой —
 *   кампании, обследования и гектары — экран читает отдельно из
 *   `GET /quarantine/prevention` (вкладка «Обходы», журнал №657). Здесь
 *   `inspections` пуст, а число обследований и охват — `null`, а не ноль;
 * * **чей контур и сколько земли — сервер.** Хозяйства строки — все
 *   землепользования контура (одно правило с кабинетом и ИИ, №652), гектары
 *   под сигналом — сводка сервера (TOTAL_LAND, №653), а не сумма на клиенте.
 */

import { api, type QueryParams, type Schemas } from '../../../api/client';
import type { QuarantineFile, QuarantineStatus } from '../../data/types';
import { type DatasetAdapter, once, paramsKey } from './adapter';
import { pageThrough } from './dataset';
import { defineMessages, type Message } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';

const m = defineMessages({
  noJournal: {
    id: 'data.quarantine.noDistrictInspections',
    defaultMessage: 'поштучного журнала обследований района база не отдаёт: обследования видны на карточке контура, а охват района профилактикой — по кампаниям на вкладке «Обходы»',
    description: 'Пояснение на экране «Карантин», почему нет общего списка обследований района (строчная фраза после «Нет данных: »); «Обходы» — название вкладки',
  },
  pest: {
    id: 'data.quarantine.cause.pests',
    defaultMessage: 'вредитель',
    description: 'Причина карантинного статуса словами: вредитель (строчная фраза)',
  },
  weed: {
    id: 'data.quarantine.cause.weeds',
    defaultMessage: 'сорняк',
    description: 'Причина карантинного статуса словами: сорняк (строчная фраза)',
  },
  disease: {
    id: 'data.quarantine.cause.diseases',
    defaultMessage: 'болезнь',
    description: 'Причина карантинного статуса словами: болезнь (строчная фраза)',
  },
  truncated: {
    id: 'data.quarantine.truncated',
    defaultMessage: 'статусы показаны не полностью: {shown} из {total} контуров с событиями',
    description: 'Оговорка к данным экрана «Карантин»: загружена только часть контуров с событиями; {shown}/{total} — числа контуров',
  },
});


type StatusRow = Schemas['QStatusRow'];

export const QUARANTINE_SET = 'quarantine.json';

/** Почему поштучного журнала обследований района на этом источнике нет. */
export function noDistrictInspections(): string {
  return tr(m.noJournal);
}

const ha = (microha: number | null | undefined): number | null =>
  microha === null || microha === undefined ? null : microha / 1_000_000;

const DIRECTION_CAUSE: Record<string, Message> = {
  pests: m.pest,
  weeds: m.weed,
  diseases: m.disease,
};

/**
 * Причина статуса словами: вид из каталога → направление (вредитель, сорняк,
 * болезнь) → текст находки. `null` — источник не назвал ничего.
 */
export function causeOf(row: Pick<StatusRow, 'problem_name' | 'problem_direction' | 'detail_note'>): string | null {
  const name = row.problem_name?.trim();
  if (name) return name;
  if (row.problem_direction) {
    const cause = DIRECTION_CAUSE[row.problem_direction];
    return cause ? tr(cause) : row.problem_direction;
  }
  const note = row.detail_note?.trim();
  return note ? note : null;
}

function statusOf(value: StatusRow['status']): QuarantineStatus['status'] {
  if (value === 'red') return 'red';
  if (value === 'green') return 'green';
  return 'watch';
}

async function readQuarantine(params: QueryParams): Promise<QuarantineFile> {
  return once(`quarantine|${paramsKey(params)}`, async () => {
    let head: Schemas['QStatusesOut'] | null = null;
    const rows = await pageThrough<StatusRow>(async (limit, offset) => {
      const reply = await api.quarantineStatuses({ ...params, limit, offset });
      if (offset === 0) head = reply;
      return reply;
    });
    const summary = head as Schemas['QStatusesOut'] | null;
    const count = (key: string) => summary?.by_status.find((row) => row.key === key)?.count ?? 0;
    const notes = [summary?.default_note, summary?.no_data_note].filter(
      (note): note is string => typeof note === 'string' && note !== '',
    );
    if (rows.truncated) {
      notes.push(tr(m.truncated, { shown: rows.items.length, total: rows.total }));
    }
    const area = (key: 'red' | 'yellow') => summary?.areas?.find((row) => row.status === key) ?? null;
    const red = area('red');
    const yellow = area('yellow');
    const land =
      red || yellow
        ? {
            red_ha: ha(red?.land_microha),
            yellow_ha: ha(yellow?.land_microha),
            flagged_ha:
              red?.land_microha == null && yellow?.land_microha == null
                ? null
                : ha((red?.land_microha ?? 0) + (yellow?.land_microha ?? 0)),
            missing: (red?.missing_contours ?? 0) + (yellow?.missing_contours ?? 0),
            signal_ha:
              red?.signal_microha == null && yellow?.signal_microha == null
                ? null
                : ha((red?.signal_microha ?? 0) + (yellow?.signal_microha ?? 0)),
            red_signal_ha: ha(red?.signal_microha),
            awaiting: summary?.awaiting_decision ?? 0,
            awaiting_ha: ha(summary?.awaiting_microha),
            awaiting_control_units: summary?.awaiting_control_units ?? 0,
            awaiting_control_contours: summary?.awaiting_control_contours ?? 0,
          }
        : null;
    return {
      summary: {
        contours_green: count('green'),
        contours_watch: count('yellow') + count('red'),
        contours_with_events: summary?.contours_with_events ?? null,
        inspections: null,
        coverage_pct: null,
        land,
        farm_hotspots: (summary?.farm_hotspots ?? []).map((row) => ({
          farmId: row.farm ? String(row.farm.id) : null,
          red: row.red,
          yellow: row.yellow,
          overdue: row.overdue,
          signalHa: ha(row.signal_microha),
          landHa: ha(row.land_microha),
          contourIds: row.contour_ids.map(String),
        })),
      },
      area_note: summary?.area_note ?? null,
      farm_rule_note: summary?.farm_rule_note ?? null,
      statuses: rows.items.map((row) => ({
        contour_id: String(row.contour_id),
        status: statusOf(row.status),
        since: row.effective_on,
        cause: causeOf(row),
        massif_id: row.massif ? String(row.massif.id) : null,
        massif_name: row.massif?.canonical_name ?? null,
        problem_name: row.problem_name ?? null,
        problem_direction: row.problem_direction ?? null,
        object_class: row.object_class ?? null,
        requirement_deadline: row.requirement_deadline ?? null,
        requirement_overdue: row.requirement_overdue ?? null,
        overdue_days: row.overdue_days ?? null,
        eliminated_on: row.eliminated_on ?? null,
        detail_note: row.detail_note ?? null,
        farm_ids: (row.farms ?? []).map((farm) => String(farm.id)),
        land_ha: ha(row.land_microha),
        on_part: row.on_part ?? false,
        signal_ha: ha(row.signal_microha),
        part_unspecified: row.part_unspecified ?? false,
        case_state: row.case_state ?? null,
        outside_cycle_reason: row.outside_cycle_reason ?? null,
        awaiting_reason: row.awaiting_reason ?? null,
        units_awaiting_control: row.units_awaiting_control ?? 0,
        decided_by: row.decided_by ?? null,
        referred_on: row.referred_on ?? null,
      })),
      inspections: [],
      inspections_note: noDistrictInspections(),
      notes,
    };
  });
}

export const quarantineAdapter: DatasetAdapter = {
  domain: 'quarantine',
  match: (name) => name === QUARANTINE_SET,
  load: (_name, params) => readQuarantine(params),
};
